import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/react-hooks';
import moment from 'moment';
import { ThemeProvider } from 'styled-components';
import assignment from '../../apollo/queries/assignment.graphql';
import assignmentUpdate from '../../apollo/mutations/assignmentUpdate.graphql';
import Spinner from '../common/Spinner/Spinner';
import { getLangCode } from '../../utils/language';
import { useErrorHandler } from '../common/ErrorHandler/ErrorHandler';
import handleGraphQLMutationErrors from '../../helpers/handleGraphQLMutationErrors';
import Assignment from './Assignment';

const dateFormat = 'DD-MM-YYYY';
const fromISOFormat = date => moment(date).format(dateFormat);
const stripTypename = obj => {
  if (obj) {
    // eslint-disable-next-line no-param-reassign
    delete obj.__typename;
  }
  return obj;
};

const AssignmentEdit = ({
  assignmentInstanceId,
  instructionalPlanId,
  onGoBack,
}) => {
  const { t, i18n } = useTranslation();
  const { handleError } = useErrorHandler();
  const handleErrorCallback = useCallback(() => {
    handleError();
  }, []);
  const { data = {}, loading } = useQuery(assignment, {
    variables: { assignmentInstanceId },
    onError: handleErrorCallback,
    fetchPolicy: 'network-only',
  });

  const assignmentData =
    data.assignment && data.assignment.assignmentInstance
      ? data.assignment.assignmentInstance
      : {};

  const [assignmentUpdateMutation] = useMutation(assignmentUpdate, {
    onError: error =>
      handleGraphQLMutationErrors(
        error,
        'NetworkError',
        getLangCode(assignmentData ? assignmentData.language : 'en'),
      ),
  });

  const attachment = assignmentData.attachment
    ? stripTypename(assignmentData.attachment)
    : null;

  if (loading) {
    return <Spinner isCentered={true} testId="assignmentSpinner" />;
  }

  if (!data.assignment) {
    handleErrorCallback();
    return <></>;
  }

  const {
    mloName,
    subjectCategory,
    classId,
    mloId,
    trimesterId,
  } = assignmentData;

  const langCode = getLangCode(assignmentData.language);
  return (
    <ThemeProvider
      theme={{
        dir: i18n.dir(),
        lang: assignmentData.language,
        langCode,
      }}
    >
      <Assignment
        onGoBack={onGoBack}
        mutation={assignmentUpdateMutation}
        title={t('Edit Assignment')}
        assignmentInstanceId={assignmentInstanceId}
        assignmentTitle={assignmentData.title}
        assignmentDescription={assignmentData.description}
        assignmentDueDate={fromISOFormat(assignmentData.dueOn)}
        assignmentGradeable={assignmentData.gradeable}
        attachmentRequired={assignmentData.attachmentRequired}
        commentRequired={assignmentData.commentRequired}
        assignmentMaxGrade={assignmentData.maxScore}
        attachment={attachment}
        mloData={{
          mloName,
          subjectCategory,
          classId,
          mloId,
          trimesterId,
          instructionalPlanId,
        }}
        language={assignmentData.language}
      />
    </ThemeProvider>
  );
};

AssignmentEdit.defaultProps = {
  onGoBack: () => {},
};

AssignmentEdit.propTypes = {
  onGoBack: PropTypes.func,
  assignmentInstanceId: PropTypes.string.isRequired,
  instructionalPlanId: PropTypes.string.isRequired,
};

export default AssignmentEdit;
